import BaseFolderType from 'owa-service/lib/contract/BaseFolderType';
import * as React from 'react';
import UsageBarPlot from './UsageBarPlot';
import UsageBarLegendRow from './UsageBarLegendRow';
import getStore from '../store/store';
import { observer } from 'mobx-react-lite';
import {
    options_mailboxCleanup_RemainingStorageLabel,
    options_mailboxCleanup_UpsellText,
    options_mailboxCleanup_UpsellTextDescription,
} from './StorageUsage.locstring.json';
import loc from 'owa-localize';
import { compareFoldersBySize } from '../util/compareFoldersBySize';
import { default as storageStore } from 'owa-storage-store/lib/store/store';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { logUsage } from 'owa-analytics';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { isFeatureEnabled } from 'owa-feature-flags';

const styles = require('./StorageUsage.scss');
const floppyDiskSvg = require('./svg/floppy-disk-illustration.svg');

export interface StorageUsageProps {
    isPremium: boolean;
}

export default observer(function StorageUsage(props: StorageUsageProps) {
    const { folders } = getStore();
    // Only Sort the top 5 items because the order is 1.Deleted, 2. Junk items (3. 4. 5) three largest folders excluding deleted and junk
    const sortedFolders = folders.slice(0, 5).sort(compareFoldersBySize);

    const [usageBars, legendPoints] = getStorageVisuals(sortedFolders);

    return (
        <div className={styles.storageContainer}>
            <div className={styles.stackedBarGraph}>{usageBars}</div>
            <div className={styles.legend}>{legendPoints}</div>
            {!props.isPremium && isConsumer() && (
                <button onClick={navigateToUpgradePage} className={styles.upsellButton}>
                    <img src={floppyDiskSvg} className={styles.floppyDiskImage} />
                    <div className={styles.upsellTextContainer}>
                        <div className={styles.upsellTitle}>
                            {loc(options_mailboxCleanup_UpsellText)}
                        </div>
                        <div className={styles.upsellDescription}>
                            {loc(options_mailboxCleanup_UpsellTextDescription)}
                        </div>
                    </div>
                </button>
            )}
        </div>
    );
});

function getStorageVisuals(sortedFolders: BaseFolderType[]) {
    const usageBars = [];
    const legendPoints = [];

    const { sendReceiveQuota, usageInBytes } = storageStore();

    let largestFoldersSum = 0;
    const totalUsage = sendReceiveQuota.Value;

    // We only want the three largest
    for (let i = 0; i < Math.min(3, sortedFolders.length); i++) {
        let currentFolder = sortedFolders[i];

        largestFoldersSum += parseInt(currentFolder.ExtendedProperty[0].Value);

        usageBars.push(
            <UsageBarPlot
                folderUsage={currentFolder.ExtendedProperty[0].Value}
                totalUsage={totalUsage}
                barIndex={i}
            />
        );

        legendPoints.push(
            <UsageBarLegendRow displayName={currentFolder.DisplayName} legendIndex={i} />
        );
    }

    if (sortedFolders.length > 3) {
        usageBars.push(
            <UsageBarPlot
                folderUsage={usageInBytes - largestFoldersSum}
                totalUsage={totalUsage}
                barIndex={3}
            />
        );

        // Put the last legend key for remaining elements
        legendPoints.push(
            <UsageBarLegendRow
                displayName={loc(options_mailboxCleanup_RemainingStorageLabel)}
                legendIndex={3}
            />
        );
    }

    // Put the last bars which follow as such. 1. bar for remaining unnamed space,  2. bar for the rest of the chart i.e to 100% length
    usageBars.push(<div className={styles.remainingStorage}></div>);

    return [usageBars, legendPoints];
}

function navigateToUpgradePage() {
    logUsage('MailboxCleanup_StorageUpsellClicked');
    const encryptedPuid = getUserConfiguration()?.SessionSettings?.EncryptedUserPuid || '';
    let upgradeUrl = 'https://go.microsoft.com/fwlink/?linkid=2127707&ep=';
    if (isFeatureEnabled('tri-leftNavUpsellAndStoragecf')) {
        upgradeUrl = 'https://go.microsoft.com/fwlink/?linkid=2129715&ep=';
    } else if (isFeatureEnabled('tri-leftNavUpsellAndStorage')) {
        upgradeUrl = 'https://go.microsoft.com/fwlink/?linkid=2129716&ep=';
    }
    window.open(upgradeUrl + encryptedPuid, '_blank');
}
